/**
 * WebSocket Event Types
 * Centralized event definitions for type-safe communication
 */
export enum WSEvent {
  // Connection lifecycle
  CONNECT = "connect",
  DISCONNECT = "disconnect",
  ERROR = "error",

  // Authentication
  AUTH_LOGIN = "auth:login",
  AUTH_LOGOUT = "auth:logout",
  AUTH_SUCCESS = "auth:success",
  AUTH_FAILED = "auth:failed",

  // Job events
  JOB_CREATED = "job:created",
  JOB_UPDATED = "job:updated",
  JOB_STARTED = "job:started",
  JOB_COMPLETED = "job:completed",
  JOB_FAILED = "job:failed",
  JOB_DELETED = "job:deleted",
  JOB_ASSIGN = "job:assign",
  JOB_ACCEPT = "job:accept",
  JOB_REJECT = "job:reject",
  JOB_PROGRESS = "job:progress",

  // Result events (public - broadcast to all users)
  RESULT_ADDED = "result:added",
  RESULT_UPDATED = "result:updated",
  RESULT_DELETED = "result:deleted",
  RESULT_JOB_CREATED = "result:job_created",     // Public: New job visible in Results
  RESULT_JOB_UPDATED = "result:job_updated",     // Public: Job status changed
  RESULT_JOB_COMPLETED = "result:job_completed", // Public: Job finished
  RESULT_JOB_DELETED = "result:job_deleted",     // Public: Job deleted from Results

  // Favorite events
  FAVORITE_ADDED = "favorite:added",
  FAVORITE_REMOVED = "favorite:removed",
  FAVORITES_CLEARED = "favorites:cleared",
  JOB_FAVORITE_ADDED = "job_favorite:added",
  JOB_FAVORITE_REMOVED = "job_favorite:removed",

  // Queue events
  QUEUE_UPDATED = "queue:updated",
  GLOBAL_PAUSE_CHANGED = "global_pause:changed",

  // Share events
  SHARE_CREATED = "share:created",
  SHARE_UPDATED = "share:updated",
  SHARE_DELETED = "share:deleted",
  SHARES_CLEARED = "shares:cleared",

  // Notification events
  NOTIFICATION = "notification",
  NOTIFICATION_CREATED = "notification:created",
  NOTIFICATION_READ = "notification:read",
  NOTIFICATION_DELETED = "notification:deleted",

  // Log events
  LOG_CREATED = "log:created",

  // System events
  CONFIG_UPDATED = "config:updated",
  STATS_UPDATED = "stats:updated",
  USER_UPDATED = "user:updated",
  USER_CONNECTED = "user:connected",
  USER_DISCONNECTED = "user:disconnected",

  // Variant events
  VARIANT_CREATED = "variant:created",
  VARIANT_APPROVED = "variant:approved",
  VARIANT_UPDATED = "variant:updated",
  VARIANT_DELETED = "variant:deleted",
  VARIANT_TYPE_CHANGED = "variant:type_changed", // Phase 21: Single variant type changed
  VARIANTS_TYPE_BULK_CHANGED = "variants:type_bulk_changed", // Phase 21: Multiple variants type changed

  // Scraper config events
  SCRAPER_CONFIG_UPDATED = "scraper_config:updated",
  SCRAPER_CONFIG_ACTIVATED = "scraper_config:activated",

  // Tag events
  TAG_CREATED = "tag:created",
  TAG_UPDATED = "tag:updated",
  TAG_DELETED = "tag:deleted",
  TAG_ADDED_TO_RESULT = "tag:added_to_result",
  TAG_REMOVED_FROM_RESULT = "tag:removed_from_result",

  // Note events
  NOTE_CREATED = "note:created",
  NOTE_UPDATED = "note:updated",
  NOTE_DELETED = "note:deleted",
  NOTES_CLEARED = "notes:cleared",

  // Proxy events
  PROXY_ASSIGNED = "proxy:assigned",
  PROXY_FAILED = "proxy:failed",
  PROXY_PROVIDER_CHANGED = "proxy_provider:changed",
  PROXY_PROVIDER_SYNC_COMPLETE = "proxy_provider:sync_complete",
  PROXY_RECOVERED = "proxy:recovered", // Phase 19: Proxy successfully recovered from disabled state
  PROXY_COOLDOWN_SET = "proxy:cooldown_set", // Phase 19: Proxy entered cooldown period
  PROXY_POOL_WARNING = "proxy:pool_warning", // Phase 19: Active proxy pool below threshold

  // Fingerprint events
  FINGERPRINT_ASSIGNED = "fingerprint:assigned",

  // Detection events
  CAPTCHA_DETECTED = "captcha:detected",
  RATE_LIMIT_DETECTED = "rate_limit:detected",

  // Scraping control events
  SCRAPING_PAUSED = "scraping:paused",
  SCRAPING_RESUMED = "scraping:resumed",
  SCRAPING_AUTO_PAUSED = "scraping:auto_paused",

  // Browser profile events
  BROWSER_PROFILE_CREATED = "browserProfile:created",
  BROWSER_PROFILE_UPDATED = "browserProfile:updated",
  BROWSER_PROFILE_DELETED = "browserProfile:deleted",
  BROWSER_PROFILE_CLEANUP = "browserProfile:cleanup",
  BROWSER_PROFILE_ERROR = "browserProfile:error",

  // Batch import events (Phase 22.3)
  BATCH_IMPORT_PROGRESS = "batch_import:progress",
  BATCH_IMPORT_PROCESSING_STARTED = "batch_import:processing_started",
  BATCH_IMPORT_COMPLETED = "batch_import:completed",
  BATCH_IMPORT_ERROR = "batch_import:error",

  // Batch list events (public - broadcast to all users)
  BATCH_LIST_CREATED = "batch_list:created",
  BATCH_LIST_UPDATED = "batch_list:updated",
  BATCH_LIST_DELETED = "batch_list:deleted",
}

/**
 * WebSocket Room Types
 * For targeted broadcasting
 */
export enum WSRoom {
  GLOBAL = "global",
  ADMIN = "admin",
  USER_PREFIX = "user:",
  JOB_PREFIX = "job:",
  SHARE_PREFIX = "share:",
}
